import translate from '@/utils/translate';
import type {
  CampaignCreatorRow,
  CreatorDeliverable,
  DeliverableState,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {TagColor} from '@/components/tag/Tag';

export interface AssetsStatus {
  label: string;
  color: TagColor;
}

function maxRoundForState(
  deliverables: CreatorDeliverable[],
  state: DeliverableState
): number | null {
  const rounds = deliverables
    .filter((d) => d.state === state)
    .map((d) => d.round ?? 1);
  return rounds.length ? Math.max(...rounds) : null;
}

// Most-actionable status for the pill, resolved most-attention-first:
// Overdue › Ready for review › Awaiting R{n} › Awaiting upload › Ready to post ›
// Posted › Complete. Null when there's nothing to show.
export function deriveAssetsStatus(
  row: CampaignCreatorRow
): AssetsStatus | null {
  const {
    isOverdue,
    requirementsMet,
    postedState,
    pendingAssetsCount,
    editsRequestedAssetsCount,
    notSubmittedCount,
    deliverables,
    maxRevisionRound,
  } = row;

  if (isOverdue) {
    return {label: translate('Overdue'), color: 'danger'};
  }

  if ((pendingAssetsCount ?? 0) > 0) {
    // Floor at 1: rounds are 1-indexed, so a count/deliverables mismatch that
    // drops the resolved round to 0 must never surface as "R0".
    const round = Math.max(
      maxRoundForState(deliverables, 'pending') ?? maxRevisionRound,
      1
    );
    return {
      label: translate('Ready for review · R{round}', {round}),
      color: 'warning',
    };
  }

  if ((editsRequestedAssetsCount ?? 0) > 0) {
    const round = Math.max(
      maxRoundForState(deliverables, 'rejected') ?? maxRevisionRound,
      1
    );
    return {
      label: translate('Awaiting R{round}', {round: round + 1}),
      color: 'neutral',
    };
  }

  if ((notSubmittedCount ?? 0) > 0) {
    return {label: translate('Awaiting upload'), color: 'neutral'};
  }

  if (postedState === 'ready_to_post') {
    return {label: translate('Ready to post'), color: 'neutral'};
  }

  if (postedState === 'posted') {
    return {label: translate('Posted'), color: 'success'};
  }

  // Terminal gigs already resolve to 'Posted' above (postedState mirrors the
  // same terminal-state check), so only a pre-post gig with everything approved
  // reaches Complete.
  if (requirementsMet) {
    return {label: translate('Complete'), color: 'success'};
  }

  return null;
}

const KIND_LABELS: Record<string, string> = {
  photo: 'Photo',
  video: 'Video',
  blog: 'Blog',
  mixed_media: 'Mixed media',
};

function kindLabel(kind: string): string {
  return translate(KIND_LABELS[kind] ?? kind);
}

function deliverableStateLabel(deliverable: CreatorDeliverable): string {
  switch (deliverable.state) {
    case 'pending':
      return translate('Ready for review · R{round}', {
        round: deliverable.round ?? 1,
      });
    case 'rejected':
      return translate('Edits requested · R{round}', {
        round: deliverable.round ?? 1,
      });
    case 'accepted':
      return translate('Approved');
    case 'processing':
      return translate('Processing');
    case 'not_submitted':
    default:
      return translate('Awaiting upload');
  }
}

export function describeDeliverable(deliverable: CreatorDeliverable): string {
  return `${kindLabel(deliverable.kind)} · ${deliverableStateLabel(deliverable)}`;
}
